import { z } from 'zod';
import { ResolutionEvidenceSchema } from './failures';
import { QualityGateResultSchema } from './quality-gates';
import { ReleaseConfidenceResponseSchema } from './release-confidence';
import { MaintenanceItemSchema } from './change-review-maintenance';
import {
  ChangeImpactAutomationImpactSchema,
  ChangeImpactContractDriftSchema,
  ChangeImpactCoverageIntelligenceSchema,
  ChangeImpactHistoricalEvidenceSchema,
  ChangeImpactMaintenanceExplainSchema,
  ChangeImpactRegressionDecisionSchema,
  ChangeImpactSelectionMetricsSchema,
  ChangeImpactVisualEvidenceSchema,
  ChangeReviewSchema,
} from './scenarios';
import { ProductionIntelSchema } from './production-intel';
import { EnvironmentIntelligenceSectionSchema } from './environment-intelligence';
import { Qd4AcceptanceResponseSchema } from './quality-decision';

/**
 * Feature 7D — portable quality evidence bundle.
 *
 * A composition of evidence Rynora already produces, shaped so it survives
 * outside the product: CI artifact, release review, audit, procurement proof.
 * Every section reuses its canonical schema — this file adds no new evidence
 * semantics, only packaging and an honest per-section status.
 */

export const EVIDENCE_BUNDLE_SCHEMA_VERSION = '1.0' as const;

/** Honest availability, mirroring the product's existing truth model. */
export const EVIDENCE_SECTION_STATUSES = ['available', 'limited', 'unavailable', 'not_applicable'] as const;
export type EvidenceSectionStatus = (typeof EVIDENCE_SECTION_STATUSES)[number];

export const EVIDENCE_BUNDLE_SECTION_NAMES = [
  'change-impact',
  'selection',
  'execution',
  'failures',
  'resolutions',
  'maintenance',
  'coverage',
  'contracts',
  'visual',
  'release-confidence',
  'environment-intelligence',
  'quality-gate',
  'links',
] as const;
export type EvidenceBundleSectionName = (typeof EVIDENCE_BUNDLE_SECTION_NAMES)[number];

export const EVIDENCE_BUNDLE_CONTEXT_KINDS = ['change_review', 'commit', 'release', 'project'] as const;
export type EvidenceBundleContextKind = (typeof EVIDENCE_BUNDLE_CONTEXT_KINDS)[number];

/** Per-section index entry in the manifest. */
export const EvidenceSectionIndexSchema = z.object({
  name: z.enum(EVIDENCE_BUNDLE_SECTION_NAMES),
  file: z.string().max(120),
  status: z.enum(EVIDENCE_SECTION_STATUSES),
  summary: z.string().max(500),
  /** Why a section is limited / unavailable. Absent when `available`. */
  note: z.string().max(300).optional(),
  itemCount: z.number().int().nonnegative(),
});
export type EvidenceSectionIndex = z.infer<typeof EvidenceSectionIndexSchema>;

/**
 * Claim locks travel with the bundle so a downstream reader cannot mistake
 * Rynora's advisory output for an authority it never claimed.
 */
export const EvidenceBundleClaimsSchema = z.object({
  claimsSafeSkip: z.literal(false),
  claimsTestsSaved: z.literal(false),
  claimsTimeSaved: z.literal(false),
  claimsMoneySaved: z.literal(false),
  claimsRiskFree: z.literal(false),
  isReleaseAuthority: z.literal(false),
  autoApply: z.literal(false),
  advisoryOnly: z.literal(true),
  /** QD-6 — RC/gates/coverage in this bundle are never QD-4 acceptance. */
  doesNotEqualQdAcceptance: z.literal(true),
  isNotQd4Acceptance: z.literal(true),
  claimsCryptographicBinding: z.literal(false),
  claimsSealedHistory: z.literal(false),
});
export type EvidenceBundleClaims = z.infer<typeof EvidenceBundleClaimsSchema>;

export const EvidenceBundleRepositorySchema = z.object({
  provider: z.string().max(40),
  identifier: z.string().max(300),
  defaultBranch: z.string().max(200).nullable(),
});
export type EvidenceBundleRepository = z.infer<typeof EvidenceBundleRepositorySchema>;

export const EvidenceBundleManifestSchema = z.object({
  schemaVersion: z.literal(EVIDENCE_BUNDLE_SCHEMA_VERSION),
  generator: z.object({
    name: z.literal('rynora'),
    component: z.literal('quality-evidence-bundle'),
    version: z.string().max(40),
  }),
  generatedAt: z.string(),
  context: z.object({
    kind: z.enum(EVIDENCE_BUNDLE_CONTEXT_KINDS),
    organizationId: z.string().uuid(),
    projectId: z.string().uuid(),
    projectName: z.string().max(200),
    commitSha: z.string().max(80).nullable(),
    branch: z.string().max(200).nullable(),
    changeReviewId: z.string().uuid().nullable(),
    releaseId: z.string().max(120).nullable(),
    repository: EvidenceBundleRepositorySchema.nullable(),
    runIds: z.array(z.string().uuid()).max(20),
  }),
  sections: z.array(EvidenceSectionIndexSchema).max(14),
  claims: EvidenceBundleClaimsSchema,
});
export type EvidenceBundleManifest = z.infer<typeof EvidenceBundleManifestSchema>;

/** Common envelope so every file is readable on its own. */
function section<T extends z.ZodTypeAny>(payload: T) {
  return z.object({
    status: z.enum(EVIDENCE_SECTION_STATUSES),
    summary: z.string().max(500),
    note: z.string().max(300).optional(),
    data: payload,
  });
}

export const EvidenceChangeImpactSchema = section(
  z.object({
    review: ChangeReviewSchema.nullable(),
    impactLevel: z.string().max(40).nullable(),
    confidence: z.string().max(40).nullable(),
    changedFiles: z.array(z.string().max(500)).max(200),
    reasons: z.array(z.string().max(300)).max(20),
    recommendedActions: z.array(z.string().max(300)).max(20),
    automationImpact: ChangeImpactAutomationImpactSchema.nullable(),
    historicalEvidence: ChangeImpactHistoricalEvidenceSchema.nullable(),
    regressionDecision: ChangeImpactRegressionDecisionSchema.nullable(),
    productionEvidence: ProductionIntelSchema.nullable(),
  }),
);
export type EvidenceChangeImpact = z.infer<typeof EvidenceChangeImpactSchema>;

export const EvidenceSelectedFlowSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string().max(300),
  score: z.number().nullable(),
  selectionKind: z.string().max(40).nullable(),
  impactLevel: z.string().max(40).nullable(),
  matchedFiles: z.array(z.string().max(500)).max(20),
  matchedRoutes: z.array(z.string().max(300)).max(20),
  why: z.array(z.string().max(200)).max(12),
  reviewRequired: z.boolean(),
});
export type EvidenceSelectedFlow = z.infer<typeof EvidenceSelectedFlowSchema>;

export const EvidenceSelectionSchema = section(
  z.object({
    metrics: ChangeImpactSelectionMetricsSchema.nullable(),
    selectedFlows: z.array(EvidenceSelectedFlowSchema).max(50),
    selectedScenarioCount: z.number().int().nonnegative(),
  }),
);
export type EvidenceSelection = z.infer<typeof EvidenceSelectionSchema>;

/** Bounded run metadata. No step results, no traces, no binaries. */
export const EvidenceRunSchema = z.object({
  runId: z.string().uuid(),
  flowId: z.string().uuid().nullable(),
  flowName: z.string().max(300),
  status: z.string().max(40),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  totalSteps: z.number().int().nonnegative(),
  passedSteps: z.number().int().nonnegative(),
  failedSteps: z.number().int().nonnegative(),
  skippedSteps: z.number().int().nonnegative(),
  healedSteps: z.number().int().nonnegative(),
  browser: z.string().max(40),
  environment: z.string().max(120),
  executionMode: z.string().max(40).nullable(),
  executionTargetKind: z.string().max(40).nullable(),
  /** Which flow version ran: `draft`, `published_v<n>`, or `branch:<id>`. */
  ranAgainst: z.string().max(120).nullable(),
  commitSha: z.string().max(80).nullable(),
  /** Reference only — trace bodies are never embedded. */
  hasTrace: z.boolean(),
  traceSizeBytes: z.number().int().nonnegative().nullable(),
  deepLink: z.string().max(500),
});
export type EvidenceRun = z.infer<typeof EvidenceRunSchema>;

export const EvidenceExecutionSchema = section(
  z.object({
    runs: z.array(EvidenceRunSchema).max(20),
    totalRuns: z.number().int().nonnegative(),
    failedRuns: z.number().int().nonnegative(),
  }),
);
export type EvidenceExecution = z.infer<typeof EvidenceExecutionSchema>;

/** Failure Intelligence, reduced to portable fields (no internal reasoning). */
export const EvidenceFailureSchema = z.object({
  failureId: z.string().uuid(),
  displayKey: z.string().max(40),
  /** Stable dedup key. Null when the source row predates fingerprinting. */
  fingerprint: z.string().max(200).nullable(),
  failureType: z.string().max(40).nullable(),
  status: z.string().max(40),
  scenarioTitle: z.string().max(300),
  stepOrder: z.number().int().nonnegative(),
  whatHappened: z.string().max(1_000),
  environment: z.string().max(120),
  browser: z.string().max(40),
  build: z.string().max(80),
  runId: z.string().uuid().nullable(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  evidenceKinds: z.array(z.string().max(40)).max(10),
  deepLink: z.string().max(500),
});
export type EvidenceFailure = z.infer<typeof EvidenceFailureSchema>;

export const EvidenceFailuresSchema = section(
  z.object({
    failures: z.array(EvidenceFailureSchema).max(20),
    openCount: z.number().int().nonnegative(),
    resolvedCount: z.number().int().nonnegative(),
  }),
);
export type EvidenceFailures = z.infer<typeof EvidenceFailuresSchema>;

/** Feature 7E — resolution + linked-issue evidence, verification preserved. */
export const EvidenceResolutionSchema = z.object({
  failureId: z.string().uuid(),
  displayKey: z.string().max(40),
  category: z.string().max(40),
  rootCause: z.string().max(500),
  verifiedBy: z.string().max(120),
  fixedBuild: z.string().max(80),
  resolvedAt: z.string().nullable(),
  verification: z.enum(['verified', 'unverified']),
  resolutionEvidence: ResolutionEvidenceSchema.nullable(),
});
export type EvidenceResolution = z.infer<typeof EvidenceResolutionSchema>;

export const EvidenceResolutionsSchema = section(
  z.object({
    resolutions: z.array(EvidenceResolutionSchema).max(20),
    verifiedCount: z.number().int().nonnegative(),
    unverifiedCount: z.number().int().nonnegative(),
    issueBackedCount: z.number().int().nonnegative(),
  }),
);
export type EvidenceResolutions = z.infer<typeof EvidenceResolutionsSchema>;

export const EvidenceMaintenanceSchema = section(
  z.object({
    explain: ChangeImpactMaintenanceExplainSchema.nullable(),
    items: z.array(MaintenanceItemSchema).max(20),
    decisionCounts: z.record(z.string(), z.number().int().nonnegative()),
  }),
);
export type EvidenceMaintenance = z.infer<typeof EvidenceMaintenanceSchema>;

export const EvidenceCoverageSchema = section(ChangeImpactCoverageIntelligenceSchema.nullable());
export type EvidenceCoverage = z.infer<typeof EvidenceCoverageSchema>;

export const EvidenceContractsSchema = section(ChangeImpactContractDriftSchema.nullable());
export type EvidenceContracts = z.infer<typeof EvidenceContractsSchema>;

export const EvidenceVisualSchema = section(ChangeImpactVisualEvidenceSchema.nullable());
export type EvidenceVisual = z.infer<typeof EvidenceVisualSchema>;

export const EvidenceReleaseConfidenceSchema = section(ReleaseConfidenceResponseSchema.nullable());
export type EvidenceReleaseConfidence = z.infer<typeof EvidenceReleaseConfidenceSchema>;

export const EvidenceEnvironmentIntelligenceSchema = section(EnvironmentIntelligenceSectionSchema);
export type EvidenceEnvironmentIntelligence = z.infer<typeof EvidenceEnvironmentIntelligenceSchema>;

export const EvidenceQualityGateSchema = section(
  z.object({
    gates: z.array(QualityGateResultSchema).max(5),
    worstVerdict: z.enum(['pass', 'warn', 'fail']).nullable(),
  }),
);
export type EvidenceQualityGate = z.infer<typeof EvidenceQualityGateSchema>;

export const EvidenceLinkSchema = z.object({
  label: z.string().max(120),
  kind: z.enum(['rynora', 'repository', 'issue', 'artifact']),
  href: z.string().max(1_000),
});
export type EvidenceLink = z.infer<typeof EvidenceLinkSchema>;

export const EvidenceLinksSchema = section(z.array(EvidenceLinkSchema).max(50));
export type EvidenceLinks = z.infer<typeof EvidenceLinksSchema>;

export const QualityEvidenceBundleSchema = z.object({
  manifest: EvidenceBundleManifestSchema,
  changeImpact: EvidenceChangeImpactSchema,
  selection: EvidenceSelectionSchema,
  execution: EvidenceExecutionSchema,
  failures: EvidenceFailuresSchema,
  resolutions: EvidenceResolutionsSchema,
  maintenance: EvidenceMaintenanceSchema,
  coverage: EvidenceCoverageSchema,
  contracts: EvidenceContractsSchema,
  visual: EvidenceVisualSchema,
  releaseConfidence: EvidenceReleaseConfidenceSchema,
  environmentIntelligence: EvidenceEnvironmentIntelligenceSchema,
  qualityGate: EvidenceQualityGateSchema,
  links: EvidenceLinksSchema,
  /**
   * QD-6 — optional QD-4 acceptance when complete build identity + frozen snapshot exist.
   * Null/omitted means export must not imply RC/gate == QD acceptance.
   */
  qdAcceptance: Qd4AcceptanceResponseSchema.nullable().optional(),
});
export type QualityEvidenceBundle = z.infer<typeof QualityEvidenceBundleSchema>;

/** Section key → file name inside `rynora-evidence/`. */
export const EVIDENCE_BUNDLE_FILES: Record<
  Exclude<keyof QualityEvidenceBundle, 'manifest' | 'qdAcceptance'>,
  `${EvidenceBundleSectionName}.json`
> = {
  changeImpact: 'change-impact.json',
  selection: 'selection.json',
  execution: 'execution.json',
  failures: 'failures.json',
  resolutions: 'resolutions.json',
  maintenance: 'maintenance.json',
  coverage: 'coverage.json',
  contracts: 'contracts.json',
  visual: 'visual.json',
  releaseConfidence: 'release-confidence.json',
  environmentIntelligence: 'environment-intelligence.json',
  qualityGate: 'quality-gate.json',
  links: 'links.json',
};

export const EvidenceExportQuerySchema = z.object({
  sha: z.string().min(7).max(80).optional(),
  releaseId: z.string().min(1).max(120).optional(),
  /** Optional — required with environment for QD-4 inclusion; never invented. */
  buildId: z.string().min(1).max(120).optional(),
  environment: z.string().min(1).max(200).optional(),
  format: z.enum(['json', 'zip']).default('json'),
});
export type EvidenceExportQuery = z.infer<typeof EvidenceExportQuerySchema>;
